import React, { useState, useEffect, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import axios from 'axios'
import ProductLayout from './ProductLayout'
import SEO from '../../components/SEO'

const API = ''

const HIDDEN_FIELDS = [
    'id', 'name', 'description', 'image_url', 'category_id', 'category_name', 
    'specifications', 'attachments', 'images', 'primary_image', 
    'operatingTemperatures', 'minOperatingTemp', 'maxOperatingTemp', 
    'inletConnections', 'outletConnections', 'lineSize', 'endConnections', 
    'availableOperators', 'fluidMedia', 'body', 'seals', 'lockWire', 
    'seatMaterial', 'operator', 'diaphragmMaterial', 'seat', 'sealMaterial', 
    'portTypes', 'cryogenic', 'productType', 'flowCoefficient',
    'crackingPressure', 'crackingPressures', 'outletPressureRange', 'MEOP', 
    'operatingPressures', 'inletPressure', 'outletPressure', 'outletSetpoint',
    'outletAdjustmentRange', 'firstStagePressureRange', 'firstStageSetpoint',
    'secondStagePressureRange', 'secondStageSetpoint', 'interstageSetpoint',
    'lineRegulation', 'externalLeakage', 'internalLeakage', 'leakage',
    'fluidTemperature', 'fluidTemperatureRange', 'pressure', 'pressureRange',
    'sizes', 'designPressures', 'proofPressure', 'burstPressure', 'weight'
]

// Maps lookup table slugs to display labels (fallback if not defined in database table_label)
const LOOKUP_LABELS = {
    line_size: 'Line Size',
    inlet_connections: 'Inlet Connections',
    outlet_connections: 'Outlet Connections',
    end_connections: 'End Connections',
    min_operating_temp: 'Min Operating Temp',
    max_operating_temp: 'Max Operating Temp',
    min_operating_pressure: 'Min Operating Pressure (PSIG)',
    max_operating_pressure: 'Max Operating Pressure (PSIG)',
    body_material: 'Body Material',
    seat_material: 'Seat Material',
    seal_material: 'Seal Material',
    fluid_media: 'Fluid Media',
    flow_coefficient: 'Flow Coefficient (Cv)',
    operators: 'Operators / Actuators',
    port_types: 'Port Types',
    service_type: 'Service Type',
    application: 'Application',
    product_type: 'Product Type',
    lock_wire: 'Lock Wire',
    diaphragm_material: 'Diaphragm Material',
    meop: 'MEOP (PSIG)',
    crackingPressure: 'Cracking Pressure (PSIG)',
    cryo: 'Cryogenic',
    min_relief_pressure: 'Minimum Relief Pressure',
    max_relief_pressure: 'Maximum Relief Pressure',
    relief_type: 'Relief Type',
    min_inlet_pressure: 'Minimum Inlet Pressure',
    max_inlet_pressure: 'Maximum Inlet Pressure',
    outletPressureRange: 'Outlet Pressure Range',
    regulator_type: 'Regulator Type',
}

function fieldLabel(name) {
    if (name === 'sku') return 'SKU'
    if (name === 'MEOP') return 'MEOP (PSIG)'
    if (name === 'productType') return 'Product Type'
    if (name === 'modelNumber') return 'Model Number'
    return name.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, s => s.toUpperCase())
}

function stripHtml(html) {
    if (!html) return ''
    return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
}

const ProductDetailPage = () => {
    const { id } = useParams()
    const [product, setProduct] = useState(null)
    const [productLookups, setProductLookups] = useState([])

    useEffect(() => {
        axios.get(`${API}/api/products/${id}`).then(r => setProduct(r.data)).catch(() => { })
        axios.get(`${API}/api/products/${id}/lookup-values`).then(r => setProductLookups(r.data)).catch(() => { })
    }, [id])

    // Group lookup values by slug for display, preserving the label from the database settings
    const lookupsBySlug = useMemo(() => {
        const grouped = {}
        productLookups.forEach(lv => {
            if (!grouped[lv.slug]) {
                grouped[lv.slug] = {
                    label: lv.table_label,
                    values: []
                }
            }
            grouped[lv.slug].values.push(lv.value)
        })
        return grouped
    }, [productLookups])

    const populatedFields = useMemo(() => {
        if (!product) return []
        const fields = Object.entries(product)
            .filter(([key, val]) => !HIDDEN_FIELDS.includes(key) && val != null && String(val).trim() !== '')
            .map(([key, val]) => ({ label: fieldLabel(key), value: String(val) }))

        // Track slugs handled by combined rows so they aren't shown individually
        const consumed = new Set()

        // Combined Temp Range from lookup values first, then direct fields
        const minTemp = lookupsBySlug['min_operating_temp']?.values.join(', ') || product.minOperatingTemp
        const maxTemp = lookupsBySlug['max_operating_temp']?.values.join(', ') || product.maxOperatingTemp
        if (minTemp || maxTemp) {
            consumed.add('min_operating_temp')
            consumed.add('max_operating_temp')
            fields.push({
                label: 'Operating Temp Range',
                value: minTemp && maxTemp ? `${minTemp} to ${maxTemp}` : (minTemp || maxTemp)
            })
        }

        // Relief Pressure (min to max)
        const minRelief = lookupsBySlug['min_relief_pressure']?.values.join(', ')
        const maxRelief = lookupsBySlug['max_relief_pressure']?.values.join(', ')
        if (minRelief || maxRelief) {
            consumed.add('min_relief_pressure')
            consumed.add('max_relief_pressure')
            fields.push({
                label: 'Relief Pressure',
                value: minRelief && maxRelief ? `${minRelief} to ${maxRelief}` : (minRelief || maxRelief)
            })
        }

        // Inlet Pressure (min to max)
        const minInlet = lookupsBySlug['min_inlet_pressure']?.values.join(', ')
        const maxInlet = lookupsBySlug['max_inlet_pressure']?.values.join(', ')
        if (minInlet || maxInlet) {
            consumed.add('min_inlet_pressure')
            consumed.add('max_inlet_pressure')
            fields.push({
                label: 'Inlet Pressure',
                value: minInlet && maxInlet ? `${minInlet} to ${maxInlet}` : (minInlet || maxInlet)
            })
        }

        // Operating Pressure Range (min to max)
        const minOpP = lookupsBySlug['min_operating_pressure']?.values.join(', ')
        const maxOpP = lookupsBySlug['max_operating_pressure']?.values.join(', ')
        if (minOpP || maxOpP) {
            consumed.add('min_operating_pressure')
            consumed.add('max_operating_pressure')
            fields.push({
                label: 'Operating Pressure Range',
                value: minOpP && maxOpP ? `${minOpP} to ${maxOpP}` : (minOpP || maxOpP)
            })
        }

        // Connections - end_connections takes priority, else inlet / outlet
        const endConns = lookupsBySlug['end_connections']?.values.join(', ')
        const inletConns = lookupsBySlug['inlet_connections']?.values.join(', ')
        const outletConns = lookupsBySlug['outlet_connections']?.values.join(', ')
        if (endConns) {
            consumed.add('end_connections')
            consumed.add('inlet_connections')
            consumed.add('outlet_connections')
            fields.push({ label: 'Connections', value: endConns })
        } else if (inletConns || outletConns) {
            consumed.add('inlet_connections')
            consumed.add('outlet_connections')
            fields.push({
                label: 'Connections',
                value: inletConns && outletConns ? `${inletConns} / ${outletConns}` : (inletConns || outletConns)
            })
        }

        // Remaining individual lookup values
        Object.entries(lookupsBySlug).forEach(([slug, group]) => {
            if (consumed.has(slug)) return
            const label = group.label || LOOKUP_LABELS[slug] || slug
            fields.push({ label, value: group.values.join(', ') })
        })


        return fields
    }, [product, lookupsBySlug])

    if (!product) {
        return (
            <ProductLayout>
                <div style={{ padding: '48px 32px', textAlign: 'center', color: 'var(--pfc-text3)' }}>
                    Loading...
                </div>
            </ProductLayout>
        )
    }

    const images = product.images || []
    const attachments = product.attachments || []
    const specs = product.specifications || []

    return (
        <ProductLayout>
            <SEO
                title={product.name}
                description={product.description ? stripHtml(product.description).slice(0, 160) : `${product.name} — precision-engineered fluid control component from Precision Fluid Controls.`}
            />
            {/* Detail Hero */}
            <div className="pfc-detail-hero">
                <div className="pfc-detail-top">
                    <div className="pfc-hero-top-inner">
                        <Link
                            to={product.category_id ? `/products/category/${product.category_id}` : '/products'}
                            className="pfc-detail-back"
                        >
                            ← Back to {product.category_name || 'Products'}
                        </Link>
                        {(product.model || product.modelNumber) && (
                            <div className="pfc-detail-model">Model {product.model || product.modelNumber}</div>
                        )}
                        <div className="pfc-detail-name">{product.name}</div>
                        {product.description && (
                            <div className="pfc-detail-desc">{stripHtml(product.description)}</div>
                        )}
                    </div>
                </div>

            </div>

            {/* Detail Content */}
            <div className="pfc-detail-content" style={{ padding: '24px 32px 48px' }}>
                <div className="pfc-detail-grid">
                    {/* Left: Images */}
                    <div className="pfc-detail-images">
                        {images.length > 0 ? (
                            images.map(img => (
                                <img
                                    key={img.id}
                                    src={`${API}/products/images/product/${img.filename}`}
                                    alt={product.name}
                                    className="pfc-detail-img"
                                />
                            ))
                        ) : product.image_url ? (
                            <img
                                src={`${API}/products/images/${product.image_url.replace(/^var\//, '')}`}
                                alt={product.name}
                                className="pfc-detail-img"
                                onError={e => { e.target.style.display = 'none' }}
                            />
                        ) : (
                            <div className="pfc-detail-img" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '3rem', minHeight: '200px' }}>
                                ⚙
                            </div>
                        )}
                    </div>

                    {/* Right: Specs */}
                    <div className="pfc-detail-specs">
                        {/* Specifications table (combined) */}
                        {(populatedFields.length > 0 || specs.length > 0) && (
                            <div>
                                <div className="pfc-section-label">Specifications</div>
                                <div className="pfc-spec-wrap">
                                    <table className="pfc-st">
                                        <thead>
                                            <tr><th>Parameter</th><th>Value</th></tr>
                                        </thead>
                                        <tbody>
                                            {populatedFields.map(f => (
                                                <tr key={f.label}>
                                                    <td>{f.label}</td>
                                                    <td>{f.value}</td>
                                                </tr>
                                            ))}
                                            {specs.map(s => (
                                                <tr key={s.id}>
                                                    <td>{s.label}</td>
                                                    <td>{s.value}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Attachments / Downloads */}
                        {attachments.length > 0 && (
                            <div>
                                <div className="pfc-section-label">Downloads</div>
                                <div className="pfc-card">
                                    <div className="pfc-card-body">
                                        {attachments.map(a => (
                                            <a
                                                key={a.id}
                                                href={`${API}/products/${a.url}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                style={{
                                                    display: 'block',
                                                    padding: '8px 0',
                                                    color: 'var(--pfc-plum3)',
                                                    fontFamily: 'var(--pfc-mono)',
                                                    fontSize: '11px',
                                                    textDecoration: 'none',
                                                    borderBottom: '1px solid var(--pfc-rule)'
                                                }}
                                            >
                                                📄 {a.name}
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* CTA */}
                        <div className="pfc-ctac" style={{ marginTop: '8px' }}>
                            <div className="pfc-ctac-icon ci-p"><i className="ti ti-messages"></i></div>
                            <div className="pfc-ctac-title">Questions about this product?</div>
                            <div className="pfc-ctac-sub">Our engineers can help with sizing, configuration, and custom modifications.</div>
                            <Link to="/contactus" className="pfc-ctac-btn cbp">Contact us</Link>
                        </div>
                    </div>
                </div>
            </div>
        </ProductLayout>
    )
}

export default ProductDetailPage
